"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, FormEvent } from "react";
import {
  Search,
  ArrowUpRight,
  ArrowRight,
  Hand,
  Heart,
  SlidersHorizontal,
  Globe2,
  Users,
  BookOpen,
  X,
  Plus,
  MapPin,
  Play,
  Flag,
  ChevronLeft, ChevronRight,
  Bookmark,
} from "lucide-react";
import { api, useApp, apiUrl } from "./provider";
import { PageTitle, Empty } from "./shell";
import { SampleShowcase } from "./sample-showcase";
import type { ShowcaseCard } from "@/lib/showcase";
import { ManagedVideo } from "./managed-video";
import { AslSearchVideo } from "./asl-search-video";
import { loadAslWords } from "../../packages/api-client/asl";
import { AslThumbnail } from "./asl-thumbnail";
import { favoritesApi } from "@/services/favorites";
import type { Word, SignVideo } from "@/lib/types";
const motifs: Record<string, React.ReactNode> = {
  greetings: <Hand />,
  people: <Users />,
  feelings: <Heart />,
  nature: <Globe2 />,
  learning: <BookOpen />,
  everyday: <Hand />,
};
export function Dictionary({ home = false, languageId, initialShowcase }: { home?: boolean; languageId?: string; initialShowcase?: ShowcaseCard[] }) {
  const { t, locale, languages, categories, error } = useApp();
  const [words, setWords] = useState<Word[]>([]),
    [stats, setStats] = useState<any>({}),
    [q, setQ] = useState(""),

    [selectedLanguage, setLanguage] = useState("asl"),
    [category, setCategory] = useState(""),
    [region, setRegion] = useState(""),
    [regionInput, setRegionInput] = useState(""),
    [sort, setSort] = useState("popular"),
    [letter, setLetter] = useState(""),
    [alphabet, setAlphabet] = useState<string[]>([]),
    [loading, setLoading] = useState(true),
    [filters, setFilters] = useState(false),
    [failed, setFailed] = useState(false),
    [failureCode, setFailureCode] = useState(""),
    [page, setPage] = useState(0),
    [resultIndex, setResultIndex] = useState(0),
    [retry, setRetry] = useState(0);
  const language = languageId || selectedLanguage;
  const apiDictionary = language === "asl";
  const scopedLanguage = languages.find((item) => item.id === languageId);
  const search = q;
  useEffect(() => {
    setPage(0);
  }, [search, language, category, region, sort, letter]);
  useEffect(() => {
    if (apiDictionary) { setAlphabet("ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")); return; }
    api(`alphabet?locale=${locale}`)
      .then((d: any) => { if(d.letters) setAlphabet(d.letters); })
      .catch(error);
  }, [locale, apiDictionary]);
  useEffect(() => {
    api("stats")
      .then(setStats)
      .catch(() => {});
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("q")) setQ(params.get("q")!);
    if (languageId) return;
    const selected = new URLSearchParams(window.location.search).get(
      "language",
    );
    if (selected) setLanguage(selected);
  }, [languageId]);
  const router = useRouter();

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    setWords(previous => (apiDictionary && page === 0) || (!apiDictionary && previous.some(word => word.provider === "voisea")) ? [] : previous);
    const timer = setTimeout(() => { (apiDictionary
      ? loadAslWords(api, { q: search, letter, page: String(page) }, controller.signal)
      : api<Word[]>(`words?${new URLSearchParams({ q: search, language, category, region, sort, letter, page: String(page) })}`))
      .then((v) => {
        if (alive) {
          setWords(page > 0 ? (prev) => [...prev, ...v] : v);
          setResultIndex(Math.floor(v.length / 2));
        }
      })
      .catch((e) => {
        if (alive) {
          setFailed(true);
          setFailureCode(e instanceof Error ? e.message : "unavailable");
          error(e);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
      alive = false;
    };
  }, [search, language, category, region, sort, letter, page, error, retry, apiDictionary]);
  function submit(e: FormEvent) {
    e.preventDefault();
    setPage(0);
  }
  return (
    <>
      {home && (
      <section className="hero">
        <div className="container">
          <div className="hero-copy" style={{ position: "relative" }}>
            <div className="eyebrow">
              <span className="small-line" />
              {t(
                "A COMMUNITY-POWERED DICTIONARY",
                "由社区驱动的鲜活词典",
              )}
            </div>
            <h1>
              {t("Know your sign.", "认识你的手语。")}
              <br />
              <span>{t("Share your world.", "分享你的世界。")}</span>
            </h1>
            <p>
              {t(
                "Discover the ways Deaf communities express themselves. Explore regional variations, learn their meanings, and add your voice to a living sign language dictionary.",
                "探索聋人社区表达自我的不同方式，了解地域变体及其含义，为鲜活的手语词典贡献您的力量。",
              )}
            </p>
            <form className="searchbox" onSubmit={submit}>
              <Search size={23} />
              <input
                aria-label={t("Search a word or meaning", "搜索词语或含义")}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("Search a word or meaning…", "搜索词语或含义…")}
              />
              {q && (
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => {
                    setQ("");

                  }}
                  aria-label={t("Clear search", "清除搜索")}
                >
                  <X size={17} />
                </button>
              )}
              <button className="button primary" type="submit">
                {t("Find a sign", "搜索手语")}
                <ArrowRight size={17} />
              </button>
            </form>
            <div className="search-hint">
              {t("Try a word:", "试试这些词语：")}{" "}
              {["Hello", "Thank you", "Friend"].map((s, i) => (
                <button
                  key={s}
                  onClick={() => {
                    setQ(locale === "en" ? s : ["你好", "谢谢", "朋友"][i]);

                  }}
                >
                  {locale === "en" ? s : ["你好", "谢谢", "朋友"][i]}
                </button>
              ))}
              <span>{t("English & Chinese search", "支持中英文搜索")}</span>
            </div>
          </div>
          <SampleShowcase hero search={q} initialCards={initialShowcase} />
        </div>
      </section>
      )}
      {home && (
        <div id="many-ways">
          <SampleShowcase search={q} onSearchChange={setQ} initialCards={initialShowcase} />
        </div>
      )}
      {languageId ? (
        <div className="container">
          <PageTitle
            eyebrow={languageId === "sgsl" ? "SgSL" : languageId.toUpperCase()}
            title={scopedLanguage ? (locale === "en" ? scopedLanguage.en : scopedLanguage.zh) : languageId.toUpperCase()}
            description={t(
              "Explore meanings and community-submitted variations in this sign language.",
              "探索这门手语的词义与社区投稿变体。",
            )}
            action={<Link href="/languages" className="button"><ChevronLeft size={17} />{t("Back to sign languages", "返回手语语言")}</Link>}
          />
        </div>
      ) : (
      <section className="language-bar">
        <div className="container">
          <span className="language-label">
            {t("CHOOSE YOUR DICTIONARY", "选择您的词典")}
          </span>
          <div role="group" aria-label={t("Sign language", "手语语言")}>
            {languages.map((l) => (
              <button
                key={l.id}
                className={`${language === l.id ? "selected" : ""} language-${l.id}`}
                aria-pressed={language === l.id}
                onClick={() => setLanguage(l.id)}
              >
                <span className="language-code">
                  {l.id === "sgsl" ? "SgSL" : l.id.toUpperCase()}
                </span>
                {locale === "en" ? l.en : l.zh}
                {language === l.id && <span className="check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      </section>
      )}
      <div className="container dictionary-layout">
        <aside className={`filters ${filters ? "show" : ""}`}>
          <div className="filter-title">
            <SlidersHorizontal size={17} />
            {t("Refine your discovery", "筛选词条")}
          </div>
          <h3>{t("Categories", "分类")}</h3>
          <button
            className={`category ${apiDictionary || category === "" ? "selected" : ""}`}
            onClick={() => setCategory("")}
          >
            <BookOpen size={17} />
            {t("All categories", "所有分类")}
            <span>{category === "" ? words.length : ""}</span>
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              disabled={apiDictionary}
              title={apiDictionary ? t("Category metadata is not supplied by the ASL API", "ASL API未提供分类信息") : undefined}
              className={`category ${!apiDictionary && category === c.id ? "selected" : ""}`}
              onClick={() => setCategory(c.id)}
            >
              {motifs[c.id] || <BookOpen size={17} />}{" "}
              {locale === "en" ? c.en : c.zh}
            </button>
          ))}
          <div className="filter-divider" />
          <h3>{t("Region or community", "地区或社区")}</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setRegion(regionInput);
            }}
            className="region-search"
          >
            <input
              aria-label={t("Region or community", "地区或社区")}
              placeholder={t("e.g. Singapore", "例如：上海")}
              disabled={apiDictionary}
              value={apiDictionary ? "" : regionInput}
              onChange={(e) => setRegionInput(e.target.value)}
            />
            <button disabled={apiDictionary} aria-label={t("Apply region filter", "应用地区筛选")}>
              <Search size={17} />
            </button>
          </form>
          {apiDictionary && <small>{t("The ASL API provides meanings and variations; category, region and popularity filters are unavailable.", "ASL API提供词义与变体，暂不支持分类、地区和热度筛选。")}</small>}
          {!apiDictionary && region && (
            <button
              className="text-button"
              onClick={() => {
                setRegion("");
                setRegionInput("");
              }}
            >
              {t("Clear region", "清除地区")} ×
            </button>
          )}
          <div className="community-note">
            <Globe2 size={24} />
            <h3>{t("Every variation belongs.", "每一种变体都有价值。")}</h3>
            <p>
              {t(
                "See how different communities express the same idea. Every variation brings a unique perspective and makes our shared language richer.",
                "探索不同社区如何表达同一个含义。每一种变体都带来了独特的视角，让我们的共同语言更加丰富。",
              )}
            </p>
            <Link href="/about">
              {t("Our guiding principle", "我们的原则")}{" "}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </aside>
        <section className="results">
          <div className="results-heading">
            <div>
              <div className="eyebrow">
                {t("THE LIVING DICTIONARY", "鲜活的词典")}
              </div>
              <h2>
                {search
                  ? t("Search results", "搜索结果")
                  : category && !apiDictionary
                    ? locale === "en"
                      ? categories.find((c) => c.id === category)?.en
                      : categories.find((c) => c.id === category)?.zh
                    : t("Discover a sign", "发现一种手语")}
              </h2>
              <p>
                {language === "asl" && search.trim() ? t("Explore meanings and every listed ASL sign variation for your search.", "探索搜索词的词义与所有列出的ASL手语变体。") : t(
                  `${words[0]?.total_count || 0} words to explore. Every meaning is a starting point.`,
                  `${words[0]?.total_count || 0}个词条，每一个含义都是探索的起点。`,
                )}
              </p>
            </div>
            <div className="result-actions">
              <button
                className="icon-button filter-toggle"
                onClick={() => setFilters(!filters)}
                aria-label={t("Toggle filters", "切换筛选")}
              >
                <SlidersHorizontal size={20} />
              </button>
              <select
                aria-label={t("Sort words", "词条排序")}
                value={apiDictionary ? "az" : sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option disabled={apiDictionary} value="popular">{t("Most liked", "最多点赞")}</option>
                <option disabled={apiDictionary} value="newest">{t("Newest first", "最新优先")}</option>
                <option value="az">{t("Alphabetical", "字母顺序")}</option>
              </select>
            </div>
          </div>
          {!home && (
            <div style={{ marginBottom: 28 }}>
              <form className="searchbox" onSubmit={submit}>
                <Search size={23} />
                <input
                  aria-label={t("Search a word or meaning", "搜索词语或含义")}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("Search a word or meaning…", "搜索词语或含义…")}
                />
                {q && (
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setQ("")}
                    aria-label={t("Clear search", "清除搜索")}
                  >
                    <X size={17} />
                  </button>
                )}
                <button className="button primary" type="submit">
                  {t("Find a sign", "搜索手语")}
                  <ArrowRight size={17} />
                </button>
              </form>
            </div>
          )}
          {language === "asl" && search.trim() && <AslSearchVideo key={search.trim()} query={search} />}
          <div style={{ display: 'flex', position: 'relative', minHeight: '600px' }}>
            <div style={{ flex: 1, paddingRight: '40px' }}>
              {loading ? (
            <div className="word-grid" aria-busy="true">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div className="skeleton" key={n} />
              ))}
            </div>
          ) : failed ? (
            <Empty
              title={
                failureCode === "rate_limit"
                  ? t(
                      "Your next discovery is one sign-in away",
                      "登录后继续探索",
                    )
                  : failureCode === "asl_authentication" ? t("The ASL API could not authenticate", "ASL API认证失败") : t("We couldn’t load these signs", "暂时无法加载手语")
              }
              description={
                failureCode === "rate_limit"
                  ? t(
                      "You’ve reached the guest browsing limit. Sign in for full access, or come back after the limit resets.",
                      "访客浏览已达限额。登录以完整访问，或在限额重置后再来。",
                    )
                  : failureCode === "asl_authentication" ? t("Check the API key configured on the server, then retry.", "请检查服务器配置的API密钥后重试。") : t("Please refresh and try again.", "请刷新后重试。")
              }
              action={
                failureCode === "rate_limit" ? <Link href="/auth" className="button primary">
                  {t("Sign in or join", "登录或加入")}
                </Link> : <button className="button primary" onClick={() => setRetry(value => value + 1)}>{t("Retry", "重试")}</button>
              }
            />
          ) : !words.length ? (
            <Empty
              title={apiDictionary ? t("No ASL entries match", "没有匹配的ASL词条") : t("A word waiting to be shared", "等待分享的新词")}
              description={apiDictionary ? t("Try another word or first letter. Only API results are shown.", "请尝试其他词语或首字母，此处仅显示API结果。") : t(
                "No entries match these filters. Try another search or suggest a new word.",
                "没有符合这些条件的词条，请尝试其他搜索或提出新词。",
              )}
              action={
                <Link className="button primary" href="/upload">
                  {t("Suggest a word", "提出新词")}
                </Link>
              }
            />
          ) : (
            <div className="word-grid">
              {words.filter(word => !apiDictionary || word.provider === "voisea").map((w) => (
                <Link
                  key={w.id}
                  href={apiDictionary ? `${languageId ? '/sign-languages/asl' : '/dictionary'}?${new URLSearchParams({ q: w.en })}` : `/word/${w.id}`}
                  onClick={apiDictionary ? (event) => { event.preventDefault(); setQ(w.en); setPage(0); } : undefined}
                  className="word-card"
                  aria-label={t(`Explore ${w.en}`, `探索 ${w.zh}`)}
                >
                  <div className={`word-art tone-${(w.id.charCodeAt(0) % 3) + 1}`}>
                    {w.provider === "voisea" ? <AslThumbnail key={w.id} src={w.thumbnail_url} label={`${w.en} ASL sign preview`} className="word-preview api-word-preview" /> : w.sample_video ? (
                      <video
                        src={w.sample_video + "#t=0.001"}
                        poster={w.provider === "voisea" ? undefined : w.sample_video.replace("/sample-videos/", "/sample-videos/posters/").replace(".mp4", ".jpg")}
                        muted
                        preload="none"
                        loop
                        playsInline
                        className="word-preview"
                        onMouseOver={(e) => {
                          const v = e.target as HTMLVideoElement;
                          v.play().catch(() => {});
                        }}
                        onMouseOut={(e) => {
                          const v = e.target as HTMLVideoElement;
                          v.pause();
                          v.currentTime = 0;
                        }}
                      />
                    ) : (
                      <div className="word-glyph">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0a2 2 0 0 0-2 2v4"/><path d="M14 10V8a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0a2 2 0 0 0-2 2v4"/><path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0a2 2 0 0 0-2 2v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/><path d="M6 14v-1.5a2 2 0 0 1 2-2v0a2 2 0 0 1 2 2v0a2 2 0 0 1 2 2v0a2 2 0 0 1 2 2v2"/></svg>
                      </div>
                    )}
                    <span className="art-translation">{locale === "en" ? w.zh : w.en}</span>
                  </div>
                  <div className="word-content">
                    <div className="word-name">
                      <h3>{locale === "en" ? w.en : w.zh || w.en}</h3>
                      <span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>
                        {" "}{w.videos || 0}
                      </span>
                    </div>
                    <p style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {locale === "en" ? w.meaning_en : w.meaning_zh || w.meaning_en}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
                    {(words[0]?.total_count || 0) > words.length && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40, width: '100%' }}>
              <button
                className="button secondary"
                style={{ padding: '12px 30px', borderRadius: '30px', fontWeight: 500, fontSize: '15px' }}
                disabled={loading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("Show more", "显示更多")}
              </button>
            </div>
          )}
            </div>
            {alphabet.length > 0 && (
              <div className="alphabet-sidebar" style={{ overflowY: 'auto', scrollbarWidth: 'none', position: 'absolute', right: '-20px', top: '10px', bottom: '10px', width: '32px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0', zIndex: 10, background: '#f0ecff', borderRadius: '16px', boxShadow: '0 2px 8px rgba(118, 97, 232, 0.15)' }}>
                <button
                  type="button"
                  className={letter === "" ? "active" : ""}
                  onClick={() => { setLetter(""); setPage(0); setQ(""); }}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 800, color: letter === "" ? 'var(--primary)' : '#998bd8', padding: '2px', transition: 'all 0.2s' }}
                >
                  {t("All", "全部")}
                </button>
                {alphabet.map(l => (
                  <button
                    key={l}
                    type="button"
                    className={letter === l ? "active" : ""}
                    onClick={() => { setLetter(l); setPage(0); setQ(""); }}
                    style={{ background: letter === l ? 'var(--primary)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 800, color: letter === l ? 'white' : '#998bd8', padding: '2px', width: '20px', height: '20px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="seed-note">
            <BookOpen size={16} />
            {apiDictionary ? t("ASL videos and meanings are supplied by the API.", "ASL视频和词义由API提供。") : t(
              "Explore supplied samples and reviewed community contributions. Every community brings a new perspective.",
              "探索提供的示例与已审核的社区投稿，每个社区都有独特的视角。",
            )}
          </div>
        </section>
      </div>
      {home && (
      <section className="contribute-banner container">
        <div>
          <div className="eyebrow">
            {t("YOUR EXPERIENCE MATTERS", "您的经验很重要")}
          </div>
          <h2>
            {t("Know another way to sign it?", "您知道另一种手语表达吗？")}
          </h2>
          <p>
            {t(
              "Share the signs of your region. Help your community be represented.",
              "分享您所在地区的手语，让您的社区被看见。",
            )}
          </p>
        </div>
        <Link href="/upload" className="button primary">
          {t("Contribute a sign", "贡献手语")}
          <ArrowUpRight size={18} />
        </Link>
      </section>
      )}
      {home && (
        <section className="container impact">
          <div>
            <strong>{stats.words ?? "—"}</strong>
            <span>{t("dictionary entries", "个词条")}</span>
          </div>
          <div>
            <strong>{stats.languages ?? "—"}</strong>
            <span>{t("sign languages", "种手语语言")}</span>
          </div>
          <div>
            <strong>{stats.videos ?? "—"}</strong>
            <span>{t("reviewed variations", "个已审核变体")}</span>
          </div>
          <div>
            <strong>{stats.contributors ?? "—"}</strong>
            <span>{t("verified contributors", "位已验证贡献者")}</span>
          </div>
        </section>
      )}
    </>
  );
}
export function WordDetail({ id }: { id: string }) {
  const { t, locale, error, message, user } = useApp(),
    [data, setData] = useState<{ word: Word; videos: SignVideo[] } | null>(
      null,
    ),
    [failed, setFailed] = useState(false),
    [report, setReport] = useState(""),
    [reason, setReason] = useState(""),
    [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedFailed, setSavedFailed] = useState(false);
  const [savedRevision, setSavedRevision] = useState(0);
  useEffect(() => {
    let alive = true;
    setSaved(false); setSavedFailed(false);
    if (!user?.verified) { setSavedLoading(false); return; }
    setSavedLoading(true);
    favoritesApi.contains(id).then(value => { if (alive) setSaved(value); })
      .catch(e => { if (alive) { setSavedFailed(true); error(e); } })
      .finally(() => { if (alive) setSavedLoading(false); });
    return () => { alive = false; };
  }, [id, user?.id, user?.verified, savedRevision, error]);
  async function toggleSaved() {
    if (saving || savedLoading) return;
    setSaving(true);
    try { await favoritesApi.set(id, !saved); setSaved(!saved); }
    catch (e) { error(e); }
    finally { setSaving(false); }
  }
  const generation = useRef(0);
  const activeId = useRef(id);
  activeId.current = id;
  const load = () => {
    if (activeId.current !== id) return Promise.resolve();
    const current = ++generation.current;
    setFailed(false);
    return api<{ word: Word; videos: SignVideo[] }>(`word/${encodeURIComponent(id)}`)
      .then(value => { if (current === generation.current) setData(value); })
      .catch((e) => {
        if (current === generation.current) { setFailed(true); error(e); }
      });
  };
  useEffect(() => {
    setData(null);
    void load();
    return () => { generation.current++; };
  }, [id, user?.id]);
  async function like(v: SignVideo) {
    setBusy(v.id);
    try {
      await api("like", { video_id: v.id, liked: !v.liked });
      await load();
    } catch (e) {
      error(e);
    } finally {
      setBusy("");
    }
  }
  if (!data)
    return (
      <div className="container">
        <Empty
          title={
            failed
              ? t("Word unavailable", "词条不可用")
              : t("Loading word…", "加载词条…")
          }
          action={failed ? <button className="button" onClick={() => void load()}>{t("Retry", "重试")}</button> : undefined}
        />
      </div>
    );
  const { word, videos } = data;
  return (
    <div className="container detail">
      <Link href="/dictionary" className="back">
        <ChevronLeft size={16} />
        {t("Back to dictionary", "返回词典")}
      </Link>
      <div className="word-header">
        <div>
          <div className="eyebrow">
            {word.language_id.toUpperCase()} · {word.id}
          </div>
          <h1>
            {locale === "en" ? word.en : word.zh}
            <span>{locale === "en" ? word.zh : word.en}</span>
          </h1>
          <p className="definition">
            {locale === "en" ? word.meaning_en : word.meaning_zh}
          </p>
          <div className="tags">
            {word.tags.map((tag) => (
              <span key={tag}>#{tag}</span>
            ))}
          </div>
          <small>
            {t("Updated", "更新于")}{" "}
            {new Date(word.updated_at).toLocaleDateString(
              locale === "zh" ? "zh-CN" : "en-US",
            )}
          </small>
        </div>
        <div className="row">
          {user ? savedFailed ? <button className="button" onClick={() => setSavedRevision(value => value + 1)}>{t("Retry saved state", "重试收藏状态")}</button> :
            <button className="button" disabled={saving || savedLoading || !user.verified} aria-pressed={saved} onClick={() => void toggleSaved()}
              title={!user.verified ? t("Verify your email to save signs", "验证邮箱后即可收藏手语") : undefined}>
              <Bookmark size={17} fill={saved ? "currentColor" : "none"} />{savedLoading || saving ? t("Please wait…", "请稍候…") : saved ? t("Saved", "已收藏") : t("Save sign", "收藏手语")}
            </button> : <Link href="/auth" className="button"><Bookmark size={17} />{t("Sign in to save", "登录后收藏")}</Link>}
        <Link href={`/upload?word=${word.id}`} className="button primary">
          <Plus size={18} />
          {t("Add Your Sign", "添加你的手语")}
        </Link>
        </div>
      </div>
      <div className="principle">
        <Globe2 size={22} />
        {t(
          "Every variation has a home. Likes show community preference, not a single correct answer.",
          "每种变体都有自己的社区。点赞代表社区偏好，并非唯一正确答案。",
        )}
      </div>
      {word.sample_video && (
        <section
          className="sample-video-panel"
          aria-labelledby="sample-video-title"
        >
          <div>
            <div className="eyebrow">
              {t("SAMPLE SIGN VIDEO", "示例手语视频")}
            </div>
            <h2 id="sample-video-title">
              {t("See the sign in motion", "观看手语示范")}
            </h2>
            <p>
              {t(
                "A supplied sample for this word. Its sign language, region, and contributor are unconfirmed. Reviewed community variations appear below.",
                "此词的提供示例，其语言、地区和贡献者尚待确认。已审核的社区变体列于下方。",
              )}
            </p>
          </div>
          <ManagedVideo
            className="sample-video"
            poster={word.sample_video
              .replace("/sample-videos/", "/sample-videos/posters/")
              .replace(".mp4", ".jpg")}
            src={word.sample_video}
            label={t(
              `${word.en} sample sign video`,
              `${word.zh}示例手语视频`,
            )}
          />
        </section>
      )}
      <PageTitle
        title={t("Community variations", "社区变体")}
        description={t(
          `${videos.length} approved variations · Most liked first`,
          `${videos.length}个已批准变体 · 按点赞数排序`,
        )}
      />
      {!videos.length ? (
        <Empty
          title={t(
            "This word is waiting for its first sign",
            "这个词正在等待第一个手语视频",
          )}
          description={t(
            "There are no reviewed videos yet. Share a variation used by your community to help the dictionary grow.",
            "暂无已审核的视频。分享您社区使用的手语变体，帮助词典成长。",
          )}
          action={
            <Link href={`/upload?word=${word.id}`} className="button primary">
              {t("Share the first variation", "分享第一个变体")}
            </Link>
          }
        />
      ) : (
        <div className="variation-grid">
          {videos.map((v) => (
            <article className="variation" key={v.id}>
              <ManagedVideo key={v.id} videoId={v.id} poster={`/api/media/${encodeURIComponent(v.id)}?thumbnail=1`}
                label={t(`Play ${word.en} — ${v.region}`, `播放${word.zh} — ${v.region}`)} />
              <div className="variation-body">
                <div className="row between">
                  <h3>{v.region}</h3>
                  <button
                    disabled={busy === v.id}
                    onClick={() => like(v)}
                    className={`like ${v.liked ? "liked" : ""}`}
                    aria-pressed={v.liked}
                  >
                    <Heart size={18} fill={v.liked ? "currentColor" : "none"} />
                    {v.likes}
                    <span className="sr-only">{t("likes", "点赞")}</span>
                  </button>
                </div>
                <p>
                  <MapPin size={15} />
                  {v.community || v.region}
                </p>
                <p>{v.explanation}</p>
                {v.notes && <p>{v.notes}</p>}
                <div className="row between">
                  <Link href={`/member/${v.user_id}`}>{v.username}</Link>
                  <button
                    className="text-button"
                    onClick={() => setReport(v.id)}
                  >
                    <Flag size={14} />
                    {t("Report", "举报")}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
      {report && (
        <div className="form-panel">
          <h3>{t("Report a concern", "举报问题")}</h3>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api("report", { video_id: report, reason });
                setReport("");
                setReason("");
                message(
                  t(
                    "Thank you. A moderator will review your report.",
                    "谢谢，审核员将查看您的举报。",
                  ),
                );
              } catch (e) {
                error(e);
              }
            }}
          >
            <label>
              {t("What should moderators review?", "您希望审核员检查什么？")}
              <textarea
                required
                value={reason}
                maxLength={2000}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div className="row">
              <button className="button primary">
                {t("Send report", "提交举报")}
              </button>
              <button
                type="button"
                className="button"
                onClick={() => setReport("")}
              >
                {t("Cancel", "取消")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
